// Configuration API centralisée
// ✨ NOUVELLE FONCTIONNALITÉ : Support fetchWithRetry pour réseaux 3G instables
import { fetchWithRetry, fetchJSONWithRetry, postJSONWithRetry } from '../utils/fetchWithRetry';

const configuredApiBaseUrl = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  ''
).replace(/\/+$/, '');

// En production, une URL relative utilise automatiquement le meme domaine que
// l'application si la variable de build n'est pas configuree sur l'hebergeur.
const defaultApiBaseUrl = process.env.NODE_ENV === 'production'
  ? ''
  : 'http://localhost:3000';

// Le parametre de version force les plateformes sociales a relire l'aperçu
// quand le visuel partage change. Il permet aussi d'invalider les anciens
// aperçus qui pointaient vers le logo general de la plateforme.
const getSharePreviewVersion = (scope, previewImageUrl = '') => {
  const value = `${scope}:${previewImageUrl}`;
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0).toString(36);
};

const API_CONFIG = {
  BASE_URL: configuredApiBaseUrl || defaultApiBaseUrl,

  // Endpoints complets
  get API_URL() {
    return `${this.BASE_URL}/api`;
  },
  
  // APIs publiques sécurisées (pour e-commerce)
  get PUBLIC_STORE_API() {
    return `${this.BASE_URL}/api/store`;
  },
  
  get PUBLIC_CUSTOMER_API() {
    return `${this.BASE_URL}/api/customer`;
  },

  get PUBLIC_MENU_API() {
    return `${this.BASE_URL}/api/menu`;
  },
  
  // Utilitaire pour construire les URLs
  url(path) {
    return `${this.BASE_URL}${path}`;
  },
  
  apiUrl(path) {
    return `${this.API_URL}${path}`;
  },
  
  // Utilitaires sécurisés pour APIs publiques
  publicStoreUrl(storeSlug, path = '') {
    return `${this.PUBLIC_STORE_API}/${storeSlug}${path}`;
  },

  publicCustomerUrl(path) {
    return `${this.PUBLIC_CUSTOMER_API}${path}`;
  },

  publicMenuUrl(menuSlug, path = '') {
    return `${this.PUBLIC_MENU_API}/${menuSlug}${path}`;
  },

  // 🔧 NOUVEAU : Helper robuste pour construire les URLs publiques de partage
  getPublicProductUrl(storeSlug, productId) {
    // Obtenir l'origine de manière robuste avec fallbacks
    let origin = '';

    try {
      // Essayer d'obtenir l'origine depuis window.location
      if (typeof window !== 'undefined' && window.location) {
        origin = window.location.origin;
      }
    } catch (e) {
      console.warn('Unable to get window.location.origin:', e);
    }

    // Fallback : utiliser BASE_URL si origin est null/undefined/vide
    if (!origin || origin === 'null' || origin === 'undefined') {
      origin = this.BASE_URL;
    }

    // Construire l'URL publique user-friendly (pas /api/store mais /store)
    return `${origin}/store/${storeSlug}/product/${productId}`;
  },

  // 🔧 NOUVEAU : Helper pour l'URL de métadonnées Open Graph (pour bots)
  getProductMetaUrl(storeSlug, productId, previewImageUrl = '') {
    let origin = '';

    try {
      if (typeof window !== 'undefined' && window.location) {
        origin = window.location.origin;
      }
    } catch (e) {
      console.warn('Unable to get window.location.origin:', e);
    }

    if (!origin || origin === 'null' || origin === 'undefined') {
      origin = this.BASE_URL;
    }

    // 🔒 URL propre et sécurisée pour le partage (n'expose pas l'architecture)
    // /p/ au lieu de /api/store/.../meta (plus court, SEO-friendly, professionnel)
    const encodedStoreSlug = encodeURIComponent(storeSlug);
    const encodedProductId = encodeURIComponent(String(productId));
    const shareVersion = getSharePreviewVersion(
      // v4 force les plateformes a relire le lien enrichi apres la suppression
      // du partage de la photo en tant que fichier joint.
      'product-preview-v4',
      previewImageUrl
    );

    return `${origin}/p/${encodedStoreSlug}/${encodedProductId}?v=${shareVersion}`;
  },

  // URL publique de boutique avec une version liee au logo. La route /store
  // sert directement les metadonnees dynamiques sans redirection intermediaire.
  getStoreMetaUrl(
    storeSlug,
    previewLogoUrl = '',
    customDomain = '',
    previewContent = ''
  ) {
    let origin = String(process.env.REACT_APP_PUBLIC_BASE_URL || '')
      .trim()
      .replace(/\/+$/, '');

    try {
      if (!origin && typeof window !== 'undefined' && window.location) {
        origin = window.location.origin;
      }
    } catch (e) {
      console.warn('Unable to get window.location.origin:', e);
    }

    if (!origin || origin === 'null' || origin === 'undefined') {
      origin = this.BASE_URL;
    }
    origin = String(origin || '').replace(/\/+$/, '');

    const domain = String(customDomain || '')
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/\/+$/, '')
      .split('/')[0];
    const isValidDomain =
      domain &&
      domain.includes('.') &&
      !domain.includes('localhost') &&
      !domain.includes('127.0.0.1');
    const encodedStoreSlug = encodeURIComponent(storeSlug);
    const shareVersion = getSharePreviewVersion(
      'store-preview-v1',
      `${previewLogoUrl}:${previewContent}`
    );
    const storeUrl = isValidDomain
      ? `https://${domain}`
      : `${origin}/store/${encodedStoreSlug}`;

    return `${storeUrl}?v=${shareVersion}`;
  },

  // ✨ NOUVEAUX HELPERS FETCH AVEC RETRY (Optimisés réseaux 3G)

  /**
   * Fetch avec retry automatique et timeout adaptatif
   * @param {string} url - URL complète ou relative (sera complétée avec BASE_URL)
   * @param {Object} options - Options fetchWithRetry
   * @returns {Promise<Response>}
   */
  async fetch(url, options = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.BASE_URL}${url}`;

    return fetchWithRetry(fullUrl, {
      timeout: 10000,
      retries: 3,
      showNotifications: true,
      ...options,
    });
  },

  /**
   * GET JSON avec retry
   * @param {string} url - URL complète ou relative
   * @param {Object} options - Options fetchWithRetry
   * @returns {Promise<Object>}
   */
  async getJSON(url, options = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.BASE_URL}${url}`;

    return fetchJSONWithRetry(fullUrl, {
      timeout: 10000,
      retries: 3,
      showNotifications: false, // Pas de notification pour GET
      ...options,
    });
  },

  /**
   * POST JSON avec retry
   * @param {string} url - URL complète ou relative
   * @param {Object} data - Données à envoyer
   * @param {Object} options - Options fetchWithRetry
   * @returns {Promise<Object>}
   */
  async postJSON(url, data, options = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.BASE_URL}${url}`;

    return postJSONWithRetry(fullUrl, data, {
      timeout: 15000, // POST plus long
      retries: 3,
      showNotifications: true,
      notifyOnRetry: true, // Notifier les retries pour POST
      ...options,
    });
  },
};

export default API_CONFIG;
